import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AmountInput } from '../../../core/shared/directives/amount-input.directive';
import {
  distinctUntilChanged,
  finalize,
  Subscription,
} from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Popup } from '../../../core/shared/components/popup/popup';
import { ToastService } from '../../../core/shared/services/toast.service';
import {
  ProposedDisbursementPayload,
  ProposedDisbursementTermPayload,
  SaveProposedDisbursementPayload,
  CraditService,
} from '../shared/services/cradit.service';
import {
  extractBaseExposure,
  extractResultColumns,
  extractResultRows,
  extractResultSummary,
  formatColumnLabel,
  formatResultValue,
  ResultSummaryItem,
} from '../shared/utils/repayment-api.util';
import { blockManualDateEntry as blockManualDateEntryUtil } from '../../../core/shared/dynamic-form/date-input.utils';
import { DropdownOption, PKL_GRP } from '../../../core/shared/models/picklist.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { CreditTermParamsDto } from '../shared/models/credit-proposal-letter.model';

/** FACILITY_TYPE ref code hidden from the Add Disbursement dropdown. */
const HIDDEN_TERM_TYPE = 30;

interface RepaymentDisbursementForm {
  prpsdDsbmntSeq: number | null;

  /**
   * Input type="month" value.
   * Format: YYYY-MM
   */
  schMth: string;

  prpsdDsbmtAmt: number | null;

  /** Repayment term type (PKL_GRP.FACILITY_TYPE) this row belongs to — drives the table it's grouped under. */
  termRefCdSeq: number;
}

interface DisbursementTableGroup {
  termRefCdSeq: number;
  title: string;
  total: number;
  rows: { row: RepaymentDisbursementForm; number: number; index: number }[];
}

/** A disbursement row payload, still tagged with the term it belongs to — grouped into `terms[]` right before sending. */
type DisbursementRowWithTerm = ProposedDisbursementPayload & {
  termRefCdSeq: number;
};

interface RepaymentPlanConfig {
  disbursements?: Array<{
    prpsdDsbmntSeq?: number | null;
    schMth?: string;
    prpsdDsbmtAmt?: number | null;
  }>;

  calculatedPlan?: unknown;

  [key: string]: unknown;
}

@Component({
  selector: 'app-repayment-plan',
  standalone: true,
  imports: [
    AmountInput,
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
  ],
  templateUrl: './repayment-plan.html',
  styleUrl: './repayment-plan.css',
})
export class RepaymentPlan
  implements OnInit, OnChanges, OnDestroy {

  private static readonly MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  readonly blockManualDateEntry = blockManualDateEntryUtil;

  @Input() config: RepaymentPlanConfig | null = null;
  @Input() editable = false;
  @Input() crdtPrpslSeq: number | null = null;
  @Input() sectionId = '';

  baseExposure = 0;

  disbursements: RepaymentDisbursementForm[] = [];

  readonly frequencyGroup = PKL_GRP.DISBURSEMENT_FREQUENCY;
  frequencyOptions: DropdownOption[] = [];

  gracePeriod = 0;
  numberOfInstallments = 0;
  arrearFlg = 0;
  frequency: number | null = null;
  totalDisbursementFromTerms = 0;
  proposedMaxExposureFromApi: number | null = null;

  loadingExposure = false;
  loadingDisbursements = false;
  loadingTermsSummary = false;
  saving = false;

  exposureErrorMessage = '';
  termsSummaryErrorMessage = '';
  errorMessage = '';
  successMessage = '';

  resultRows: Record<string, unknown>[] = [];
  resultColumns: string[] = [];
  resultSummary: ResultSummaryItem[] = [];

  showDeletePopup = false;
  deleteMessage = '';

  termOptions: DropdownOption[] = [];
  showAddDisbursementPopup = false;
  draftTermRefCdSeq: number | null = null;
  draftTermError = '';

  private initialized = false;
  private loadedExposureSequence: number | null = null;
  private loadedDisbursementsSequence: number | null = null;
  private loadedTermsSummarySequence: number | null = null;
  private removedDisbursements: DisbursementRowWithTerm[] = [];
  private pendingRemoveIndex: number | null = null;

  /**
   * Disbursement window (YYYY-MM) per repayment term (`typ`), taken from
   * `dsbStrtDt` / `dsbEndDt` of GET /crdt-prpsl/get-crdt-trm-params/{seq}.
   */
  private disbursementWindowByTerm = new Map<
    number,
    { startMonth: string; endMonth: string }
  >();

  private readonly subscriptions =
    new Subscription();

  constructor(
    private creditService: CraditService,
    private toast: ToastService,
    private pickListService: PickListService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initialized = true;

    this.applyConfig();
    this.loadTermOptions();
    this.loadFrequencyOptions();

    const saveTriggerSubscription =
      this.creditService.saveSectionTrigger$.subscribe(
        (sectionId) => {
          if (sectionId === this.sectionId) {
            this.saveProposedDisbursements();
          }
        },
      );

    this.subscriptions.add(saveTriggerSubscription);

    const sequenceSubscription =
      this.creditService.crdtPrpslSeq$
        .pipe(
          distinctUntilChanged(),
        )
        .subscribe((sequence) => {
          const normalizedSequence =
            Number(sequence ?? 0);

          if (
            !Number.isFinite(normalizedSequence) ||
            normalizedSequence <= 0
          ) {
            return;
          }

          this.crdtPrpslSeq =
            normalizedSequence;

          this.loadBaseExposure(
            normalizedSequence,
          );

          this.loadTermsSummary(
            normalizedSequence,
          );

          this.loadProposedDisbursements(
            normalizedSequence,
          );
        });

    this.subscriptions.add(
      sequenceSubscription,
    );

    const currentSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (currentSequence > 0) {
      this.crdtPrpslSeq =
        currentSequence;

      this.loadBaseExposure(
        currentSequence,
      );

      this.loadTermsSummary(
        currentSequence,
      );

      this.loadProposedDisbursements(
        currentSequence,
      );
    }
  }

  ngOnChanges(
    changes: SimpleChanges,
  ): void {
    if (changes['config']) {
      this.applyConfig();
    }

    if (
      this.initialized &&
      changes['crdtPrpslSeq']
    ) {
      const sequence =
        Number(
          this.crdtPrpslSeq ?? 0,
        );

      if (sequence > 0) {
        this.loadBaseExposure(
          sequence,
        );

        this.loadTermsSummary(
          sequence,
        );

        this.loadProposedDisbursements(
          sequence,
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get totalDisbursementAmount(): number {
    return this.totalDisbursementFromTerms;
  }

  get proposedMaxExposure(): number {
    return this.proposedMaxExposureFromApi ?? 0;
  }

  get selectedFrequencyLabel(): string {
    if (this.frequency === null || this.frequency === undefined) {
      return '-';
    }

    const selectedOption = this.frequencyOptions.find(
      (option) => Number(option.value) === Number(this.frequency),
    );

    return selectedOption?.label ?? String(this.frequency);
  }

  loadFrequencyOptions(): void {
    const frequencySubscription = this.pickListService
      .getOptions(this.frequencyGroup)
      .subscribe({
        next: (options: DropdownOption[]) => {
          this.frequencyOptions = (Array.isArray(options) ? options : [])
            .filter(
              (option) =>
                option.original?.refCdActvFlg !== false &&
                Number(option.original?.delFlg ?? 0) !== 1,
            )
            .map(
              (option): DropdownOption => ({
                ...option,
                value: Number(option.value),
              }),
            );
        },

        error: (error: unknown) => {
          console.error('Failed to load disbursement frequency:', error);
        },
      });

    this.subscriptions.add(frequencySubscription);

    this.pickListService.loadCodes([this.frequencyGroup]);
  }

  refreshInputs(): void {
    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService.getCrdtPrpslSeq() ??
        0,
      );

    if (proposalSequence <= 0) {
      this.exposureErrorMessage =
        this.translate.instant('REPAYMENT_PLAN.PROPOSAL_SEQUENCE_UNAVAILABLE');

      return;
    }

    this.loadBaseExposure(proposalSequence, true);
    this.loadTermsSummary(proposalSequence, true);
  }

  loadTermsSummary(
    sequence?: number,
    forceReload = false,
  ): void {
    const proposalSequence =
      Number(
        sequence ??
        this.crdtPrpslSeq ??
        this.creditService.getCrdtPrpslSeq() ??
        0,
      );

    if (
      !Number.isFinite(proposalSequence) ||
      proposalSequence <= 0
    ) {
      return;
    }

    if (
      !forceReload &&
      this.loadedTermsSummarySequence === proposalSequence
    ) {
      return;
    }

    this.loadingTermsSummary = true;
    this.termsSummaryErrorMessage = '';

    const termsSubscription = this.creditService
      .getCreditTermParameters(proposalSequence)
      .pipe(
        finalize(() => {
          this.loadingTermsSummary = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          const summary = this.extractTermsSummary(response);

          this.gracePeriod = summary?.grcPrd ?? 0;
          this.numberOfInstallments = summary?.noOfInstlmnt ?? 0;
          this.frequency = summary?.rpymntFrqncy ?? null;
          this.arrearFlg = summary?.arrearFlg ?? 0;
          this.totalDisbursementFromTerms = summary?.totalDisbursementAmount ?? 0;
          this.proposedMaxExposureFromApi = summary?.prpsdExposure ?? null;
          this.disbursementWindowByTerm =
            this.extractDisbursementWindows(response);
          this.syncFirstSlotMonths();

          this.loadedTermsSummarySequence = proposalSequence;
        },

        error: (error: any) => {
          this.termsSummaryErrorMessage =
            error?.error?.message ??
            this.translate.instant('REPAYMENT_PLAN.LOAD_INPUTS_FAILED');

          console.error(
            'Get Repayment Terms API error:',
            error,
          );
        },
      });

    this.subscriptions.add(termsSubscription);
  }

  private extractTermsSummary(response: any): {
    grcPrd: number;
    noOfInstlmnt: number;
    rpymntFrqncy: number;
    arrearFlg: number;
    totalDisbursementAmount: number;
    prpsdExposure: number | null;
  } | null {
    const source =
      response?.data &&
      typeof response.data === 'object' &&
      !Array.isArray(response.data)
        ? response.data
        : response;

    if (!source || typeof source !== 'object') {
      return null;
    }

    const crdtTrmParamsLst = Array.isArray(source.crdtTrmParamsLst)
      ? source.crdtTrmParamsLst
      : [];

    // Sum of dsbmntAmount across all repayment terms (Terms Finance +
    // Islamic Finance) configured in Repayment Terms.
    const totalDisbursementAmount = crdtTrmParamsLst
      .filter((row: any) => Number(row?.delFlg ?? 0) !== 1)
      .reduce(
        (sum: number, row: any) => sum + Number(row?.dsbmntAmount ?? 0),
        0,
      );

    const prpsdExposureRaw = source.prpsdExposure ?? source.prpsdExpsr;
    const prpsdExposure =
      prpsdExposureRaw === null ||
      prpsdExposureRaw === undefined ||
      prpsdExposureRaw === ''
        ? null
        : Number(prpsdExposureRaw);

    return {
      grcPrd: Number(source.grcPrd ?? 0),
      noOfInstlmnt: Number(source.noOfInstlmnt ?? 0),
      rpymntFrqncy: Number(source.rpymntFrqncy ?? 0),
      arrearFlg: Number(source.arrearFlg) === 1 ? 1 : 0,
      totalDisbursementAmount,
      prpsdExposure: prpsdExposure !== null && Number.isFinite(prpsdExposure)
        ? prpsdExposure
        : null,
    };
  }

  private extractDisbursementWindows(
    response: any,
  ): Map<number, { startMonth: string; endMonth: string }> {
    const source =
      response?.data &&
      typeof response.data === 'object' &&
      !Array.isArray(response.data)
        ? response.data
        : response;

    const windows = new Map<number, { startMonth: string; endMonth: string }>();

    (Array.isArray(source?.crdtTrmParamsLst) ? source.crdtTrmParamsLst : [])
      .filter((row: any) => Number(row?.delFlg ?? 0) !== 1)
      .forEach((row: any) => {
        const term = Number(row?.typ ?? 0);

        if (!term) {
          return;
        }

        windows.set(term, {
          startMonth: this.toMonthInput(row?.dsbStrtDt),
          endMonth: this.toMonthInput(row?.dsbEndDt),
        });
      });

    return windows;
  }

  getDisbursementStartMonth(termRefCdSeq: number): string {
    return (
      this.disbursementWindowByTerm.get(Number(termRefCdSeq ?? 0))
        ?.startMonth ?? ''
    );
  }

  getDisbursementEndMonth(termRefCdSeq: number): string {
    return (
      this.disbursementWindowByTerm.get(Number(termRefCdSeq ?? 0))
        ?.endMonth ?? ''
    );
  }

  // First slot of each term's table is pinned to that term's disbursement
  // start month and can't be edited.
  isLockedFirstSlot(row: RepaymentDisbursementForm): boolean {
    if (!this.getDisbursementStartMonth(row.termRefCdSeq)) {
      return false;
    }

    return (
      this.disbursements.find(
        (item) =>
          Number(item.termRefCdSeq ?? 0) === Number(row.termRefCdSeq ?? 0),
      ) === row
    );
  }

  isAfterDisbursementEnd(row: RepaymentDisbursementForm): boolean {
    const endMonth = this.getDisbursementEndMonth(row.termRefCdSeq);

    return (
      !!endMonth &&
      this.isValidMonth(row.schMth) &&
      row.schMth > endMonth
    );
  }

  isBeforeDisbursementStart(row: RepaymentDisbursementForm): boolean {
    const startMonth = this.getDisbursementStartMonth(row.termRefCdSeq);

    return (
      !!startMonth &&
      this.isValidMonth(row.schMth) &&
      row.schMth < startMonth
    );
  }

  isOutsideDisbursementWindow(row: RepaymentDisbursementForm): boolean {
    return (
      this.isBeforeDisbursementStart(row) ||
      this.isAfterDisbursementEnd(row)
    );
  }

  // A term's slots can only be added once its disbursement start date has
  // been saved in Repayment Terms — otherwise the first slot can't be patched.
  private ensureDisbursementWindow(termRefCdSeq: number): boolean {
    if (this.getDisbursementStartMonth(termRefCdSeq)) {
      return true;
    }

    this.toast.error(
      this.translate.instant('REPAYMENT_PLAN.SAVE_DISBURSEMENT_IN_TERMS_FIRST', {
        term:
          this.getTermLabel(termRefCdSeq) ||
          this.translate.instant('REPAYMENT_PLAN.PROPOSED_DISBURSEMENTS'),
      }),
    );

    return false;
  }

  private syncFirstSlotMonths(): void {
    if (!this.editable) {
      return;
    }

    const seenTerms = new Set<number>();

    this.disbursements.forEach((row) => {
      const term = Number(row.termRefCdSeq ?? 0);

      if (seenTerms.has(term)) {
        return;
      }

      seenTerms.add(term);

      const startMonth = this.getDisbursementStartMonth(term);

      if (startMonth) {
        row.schMth = startMonth;
      }
    });
  }

  loadBaseExposure(
    sequence?: number,
    forceReload = false,
  ): void {
    const proposalSequence =
      Number(
        sequence ??
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (
      !Number.isFinite(
        proposalSequence,
      ) ||
      proposalSequence <= 0
    ) {
      this.baseExposure = 0;

      this.exposureErrorMessage =
        this.translate.instant('REPAYMENT_PLAN.PROPOSAL_SEQUENCE_UNAVAILABLE');

      return;
    }

    if (
      !forceReload &&
      this.loadedExposureSequence ===
        proposalSequence
    ) {
      return;
    }

    this.loadingExposure = true;
    this.exposureErrorMessage = '';

    const exposureSubscription =
      this.creditService
        .getPreviousMaxExposure(
          proposalSequence,
        )
        .pipe(
          finalize(() => {
            this.loadingExposure =
              false;
          }),
        )
        .subscribe({
          next: (response: unknown) => {
            const exposure =
              extractBaseExposure(
                response,
              );

            if (exposure === null) {
              this.baseExposure = 0;

              this.exposureErrorMessage =
                this.translate.instant('REPAYMENT_PLAN.EXPOSURE_NOT_RETURNED');

              return;
            }

            this.baseExposure =
              exposure;

            this.loadedExposureSequence =
              proposalSequence;
          },

          error: (error: any) => {
            this.baseExposure = 0;

            this.exposureErrorMessage =
              error?.error?.message ??
              this.translate.instant('REPAYMENT_PLAN.LOAD_EXPOSURE_FAILED');

            console.error(
              'Previous maximum exposure API error:',
              error,
            );
          },
        });

    this.subscriptions.add(
      exposureSubscription,
    );
  }

  loadProposedDisbursements(
    sequence?: number,
    forceReload = false,
  ): void {
    const proposalSequence =
      Number(
        sequence ??
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (
      !Number.isFinite(
        proposalSequence,
      ) ||
      proposalSequence <= 0
    ) {
      return;
    }

    if (
      !forceReload &&
      this.loadedDisbursementsSequence ===
        proposalSequence
    ) {
      return;
    }

    this.loadingDisbursements = true;

    const disbursementsSubscription =
      this.creditService
        .getProposedDisbursements(
          proposalSequence,
        )
        .pipe(
          finalize(() => {
            this.loadingDisbursements =
              false;
          }),
        )
        .subscribe({
          next: (response: unknown) => {
            const items =
              this.extractDisbursementItems(
                response,
              );

            if (items.length > 0) {
              // Backend returns rows newest-first (by prpsdDsbmntSeq); keep
              // `disbursements` oldest-first so save order and row numbers
              // stay stable — display order is reversed separately via
              // `groupedDisplayDisbursements`.
              this.disbursements =
                items
                  .map(
                    (item) =>
                      this.mapDisbursementItem(
                        item,
                      ),
                  )
                  .sort(
                    (a, b) =>
                      (a.prpsdDsbmntSeq ?? 0) -
                      (b.prpsdDsbmntSeq ?? 0),
                  );

              this.syncFirstSlotMonths();
            }

            this.loadedDisbursementsSequence =
              proposalSequence;
          },

          error: (error: any) => {
            console.error(
              'Get proposed disbursements API error:',
              error,
            );
          },
        });

    this.subscriptions.add(
      disbursementsSubscription,
    );
  }

  loadTermOptions(): void {
    const termsSubscription = this.pickListService
      .loadCodesAsync([PKL_GRP.FACILITY_TYPE])
      .subscribe({
        next: () => {
          // Repayment term types come from ref code group 13 (FACILITY_TYPE).
          this.termOptions = [
            ...this.pickListService.getOptionsSync(PKL_GRP.FACILITY_TYPE),
          ].sort(
            (first, second) =>
              Number(first.original?.sortOrder ?? 0) -
              Number(second.original?.sortOrder ?? 0),
          );
        },
        error: (error: any) => {
          console.error('Unable to load repayment term picklist:', error);
        },
      });

    this.subscriptions.add(termsSubscription);
  }

  // Disbursements grouped by the selected repayment term, each rendered as
  // its own table with a title driven by that term. Rows keep their entry
  // order (S# 1, 2, 3…), so a newly added disbursement appears at the bottom.
  get groupedDisplayDisbursements(): DisbursementTableGroup[] {
    const groups = new Map<
      number,
      { row: RepaymentDisbursementForm; index: number }[]
    >();

    this.disbursements.forEach((row, index) => {
      const key = Number(row.termRefCdSeq ?? 0);
      const entries = groups.get(key) ?? [];

      entries.push({ row, index });
      groups.set(key, entries);
    });

    return Array.from(groups.entries()).map(([termRefCdSeq, entries]) => ({
      termRefCdSeq,
      title: this.getDisbursementTableTitle(termRefCdSeq),
      total: entries.reduce(
        (sum, entry) => sum + Number(entry.row.prpsdDsbmtAmt ?? 0),
        0,
      ),
      rows: entries.map((entry, position) => ({
        row: entry.row,
        number: position + 1,
        index: entry.index,
      })),
    }));
  }

  getDisbursementTableTitle(termRefCdSeq: number): string {
    const label = this.getTermLabel(termRefCdSeq);

    return label
      ? this.translate.instant('REPAYMENT_PLAN.TERM_DISBURSEMENTS', { term: label })
      : this.translate.instant('REPAYMENT_PLAN.PROPOSED_DISBURSEMENTS');
  }

  private getTermLabel(termRefCdSeq: number): string {
    if (!termRefCdSeq) {
      return '';
    }

    const option = this.termOptions.find(
      (item) => Number(item.value) === Number(termRefCdSeq),
    );

    return option?.label ? String(option.label).trim() : '';
  }

  trackByDisbursementGroup(
    index: number,
    group: DisbursementTableGroup,
  ): number {
    return group.termRefCdSeq;
  }

  // Repayment terms that don't already have a Proposed Disbursements table —
  // the "+ Add Disbursement" popup only ever creates a brand-new table.
  // Adding more rows to an existing table happens via that table's own "+" icon.
  get availableTermOptionsForDisbursement(): DropdownOption[] {
    const usedTermRefCdSeqs = new Set(
      this.disbursements.map((row) => Number(row.termRefCdSeq ?? 0)),
    );

    // Facility type 30 is never offered as a new repayment term.
    return this.termOptions.filter(
      (option) =>
        Number(option.value) !== HIDDEN_TERM_TYPE &&
        !usedTermRefCdSeqs.has(Number(option.value)),
    );
  }

  // Adds a new empty row directly to an already-created table, without going
  // through the "+ Add Disbursement" popup.
  addDisbursementRow(termRefCdSeq: number): void {
    if (!this.editable || !this.ensureDisbursementWindow(termRefCdSeq)) {
      return;
    }

    this.disbursements = [
      ...this.disbursements,
      this.createEmptyDisbursement(termRefCdSeq),
    ];

    this.syncFirstSlotMonths();
  }

  // Groups active + removed disbursement rows by their repayment term into
  // the `terms[]` shape the save API expects.
  private buildTermsPayload(
    rows: DisbursementRowWithTerm[],
  ): ProposedDisbursementTermPayload[] {
    const groups = new Map<number, ProposedDisbursementPayload[]>();

    rows.forEach(({ termRefCdSeq, ...disbursement }) => {
      const key = Number(termRefCdSeq ?? 0);
      const list = groups.get(key) ?? [];

      list.push(disbursement);
      groups.set(key, list);
    });

    return Array.from(groups.entries()).map(
      ([rpymntTerm, disbursements]) => ({
        rpymntTerm,
        delflg: 0,
        disbursements,
      }),
    );
  }

  openAddDisbursementPopup(): void {
    if (!this.editable || !this.availableTermOptionsForDisbursement.length) {
      return;
    }

    this.draftTermRefCdSeq = null;
    this.draftTermError = '';
    this.showAddDisbursementPopup = true;
  }

  closeAddDisbursementPopup(): void {
    this.showAddDisbursementPopup = false;
    this.draftTermRefCdSeq = null;
    this.draftTermError = '';
  }

  confirmAddDisbursementTerm(): void {
    if (!this.draftTermRefCdSeq) {
      this.draftTermError = 'REPAYMENT_PLAN.SELECT_TERM_TO_CONTINUE';
      return;
    }

    if (!this.ensureDisbursementWindow(this.draftTermRefCdSeq)) {
      this.closeAddDisbursementPopup();
      return;
    }

    this.disbursements = [
      ...this.disbursements,
      this.createEmptyDisbursement(this.draftTermRefCdSeq),
    ];

    this.syncFirstSlotMonths();
    this.closeAddDisbursementPopup();
  }

  addDisbursement(): void {
    if (!this.editable) {
      return;
    }

    this.openAddDisbursementPopup();
  }

  requestRemoveDisbursement(
    index: number,
  ): void {
    if (!this.editable) {
      return;
    }

    this.pendingRemoveIndex = index;
    this.deleteMessage =
      'REPAYMENT_PLAN.REMOVE_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmRemoveDisbursement(): void {
    if (this.pendingRemoveIndex === null) {
      return;
    }

    this.removeDisbursement(this.pendingRemoveIndex);
    this.pendingRemoveIndex = null;
  }

  private removeDisbursement(
    index: number,
  ): void {
    if (!this.editable) {
      return;
    }

    const removedRow =
      this.disbursements[index];

    if (
      removedRow &&
      removedRow.prpsdDsbmntSeq !== null &&
      removedRow.prpsdDsbmntSeq !== undefined
    ) {
      this.removedDisbursements.push({
        prpsdDsbmntSeq:
          removedRow.prpsdDsbmntSeq,

        schMth:
          this.monthToApiDate(
            removedRow.schMth,
          ),

        prpsdDsbmntAmt:
          Number(
            removedRow.prpsdDsbmtAmt ?? 0,
          ),

        termRefCdSeq: Number(removedRow.termRefCdSeq ?? 0),

        delFlg: 1,
      });
    }

    this.disbursements =
      this.disbursements.filter(
        (
          _row:
            RepaymentDisbursementForm,
          rowIndex:
            number,
        ) =>
          rowIndex !== index,
      );

    this.syncFirstSlotMonths();
  }

  saveProposedDisbursements(): void {
    if (!this.editable) {
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';

    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (
      !Number.isFinite(
        proposalSequence,
      ) ||
      proposalSequence <= 0
    ) {
      this.errorMessage =
        this.translate.instant('REPAYMENT_PLAN.PROPOSAL_SEQUENCE_REQUIRED');

      this.toast.error(this.errorMessage);
      this.reportValidationError(this.errorMessage);
      return;
    }

    const activeDisbursements:
      DisbursementRowWithTerm[] = [];

    for (
      let index = 0;
      index <
      this.disbursements.length;
      index++
    ) {
      const row =
        this.disbursements[index];

      const isNewRow =
        row.prpsdDsbmntSeq === null ||
        row.prpsdDsbmntSeq === undefined;

      const isEmptyRow =
        !row.schMth &&
        (row.prpsdDsbmtAmt === null ||
          row.prpsdDsbmtAmt === undefined);

      if (isNewRow && isEmptyRow) {
        this.errorMessage =
          this.translate.instant('REPAYMENT_PLAN.ROW_DATA_REQUIRED', { row: index + 1 });

        this.toast.error(this.errorMessage);
        this.reportValidationError(this.errorMessage);
        return;
      }

      if (
        !this.isValidMonth(
          row.schMth,
        )
      ) {
        this.errorMessage =
          this.translate.instant('REPAYMENT_PLAN.ROW_MONTH_REQUIRED', { row: index + 1 });

        this.toast.error(this.errorMessage);
        this.reportValidationError(this.errorMessage);
        return;
      }

      if (this.isBeforeDisbursementStart(row)) {
        this.errorMessage =
          this.translate.instant('REPAYMENT_PLAN.ROW_MONTH_BEFORE_START', {
            row: index + 1,
            startMonth: this.formatMonthLabel(
              this.getDisbursementStartMonth(row.termRefCdSeq),
            ),
          });

        this.toast.error(this.errorMessage);
        this.reportValidationError(this.errorMessage);
        return;
      }

      if (this.isAfterDisbursementEnd(row)) {
        this.errorMessage =
          this.translate.instant('REPAYMENT_PLAN.ROW_MONTH_AFTER_END', {
            row: index + 1,
            endMonth: this.formatMonthLabel(
              this.getDisbursementEndMonth(row.termRefCdSeq),
            ),
          });

        this.toast.error(this.errorMessage);
        this.reportValidationError(this.errorMessage);
        return;
      }

      const amount =
        Number(
          row.prpsdDsbmtAmt,
        );

      if (
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        this.errorMessage =
          this.translate.instant('REPAYMENT_PLAN.ROW_AMOUNT_INVALID', { row: index + 1 });

        this.toast.error(this.errorMessage);
        this.reportValidationError(this.errorMessage);
        return;
      }

      activeDisbursements.push({
        prpsdDsbmntSeq:
          row.prpsdDsbmntSeq,

        schMth:
          this.monthToApiDate(
            row.schMth,
          ),

        prpsdDsbmntAmt:
          amount,

        termRefCdSeq: Number(row.termRefCdSeq ?? 0),

        delFlg: 0,
      });
    }

    const payload:
      SaveProposedDisbursementPayload = {
      crdtPrpslSeq:
        proposalSequence,

      terms: this.buildTermsPayload([
        ...activeDisbursements,
        ...this.removedDisbursements,
      ]),
    };

    this.saving = true;
    this.clearResult();

    // Backend rejects the save when a term's tranche disbursements don't add
    // up to that term's configured disbursement amount (Repayment Terms
    // section) — check the same thing here first so the user gets an
    // immediate, specific error instead of a failed API round-trip.
    const verifySubscription = this.creditService
      .getCreditTermParameters(proposalSequence)
      .subscribe({
        next: (termParams) => {
          const mismatchError = this.findDisbursementTotalMismatch(
            payload.terms,
            termParams,
          );

          if (mismatchError) {
            this.saving = false;

            this.errorMessage = mismatchError;
            this.toast.error(mismatchError);
            this.reportValidationError(mismatchError);
            return;
          }

          this.executeSaveProposedDisbursements(payload, activeDisbursements);
        },

        error: (error: any) => {
          this.saving = false;

          this.errorMessage =
            error?.error?.message ??
            this.translate.instant('REPAYMENT_PLAN.VERIFY_FAILED');

          this.toast.error(this.errorMessage);
          this.reportValidationError(this.errorMessage);

          console.error(
            'Get Repayment Terms API error:',
            error,
          );
        },
      });

    this.subscriptions.add(verifySubscription);
  }

  /**
   * Compares each term's total proposed disbursement amount (across all its
   * tranche rows) against that term's `dsbmntAmount` from
   * GET /crdt-prpsl/get-crdt-trm-params/{crdtPrpslSeq} — the same rule the
   * backend enforces on save. Returns an error message if any term's totals
   * don't match, or null if everything lines up.
   */
  private findDisbursementTotalMismatch(
    terms: ProposedDisbursementTermPayload[],
    termParams: CreditTermParamsDto,
  ): string | null {
    const configuredAmountByTerm = new Map<number, number>();

    (termParams?.crdtTrmParamsLst ?? [])
      .filter((row) => Number(row?.delFlg ?? 0) !== 1)
      .forEach((row) => {
        configuredAmountByTerm.set(
          Number(row.typ),
          Number(row.dsbmntAmount ?? 0),
        );
      });

    for (const term of terms) {
      const activeDisbursements = term.disbursements.filter(
        (disbursement) => Number(disbursement.delFlg ?? 0) !== 1,
      );

      // A term whose tranches are all being deleted (delFlg 1) — e.g. its
      // repayment term was removed in Repayment Terms — is only in the
      // payload so the backend deletes those rows; it has no configured
      // amount to match any more.
      if (activeDisbursements.length === 0) {
        continue;
      }

      const proposedTotal = activeDisbursements
        .reduce(
          (sum, disbursement) =>
            sum + Number(disbursement.prpsdDsbmntAmt ?? 0),
          0,
        );

      const termLabel =
        this.getTermLabel(term.rpymntTerm) ||
        this.translate.instant('REPAYMENT_PLAN.PROPOSED_DISBURSEMENTS');
      const configuredAmount = configuredAmountByTerm.get(
        Number(term.rpymntTerm),
      );

      if (configuredAmount === undefined) {
        return this.translate.instant('REPAYMENT_PLAN.NO_CONFIGURED_AMOUNT', { term: termLabel });
      }

      if (Math.abs(proposedTotal - configuredAmount) > 0.01) {
        return this.translate.instant('REPAYMENT_PLAN.TOTAL_MISMATCH', {
          proposed: proposedTotal,
          configured: configuredAmount,
          term: termLabel,
        });
      }
    }

    return null;
  }

  private executeSaveProposedDisbursements(
    payload: SaveProposedDisbursementPayload,
    activeDisbursements: DisbursementRowWithTerm[],
  ): void {
    const saveSubscription =
      this.creditService
        .saveProposedDisbursements(
          payload,
        )
        .pipe(
          finalize(() => {
            this.saving = false;
          }),
        )
        .subscribe({
          next: (response: unknown) => {
            // A 200 can still carry `{ error }` — ErrorInterceptor already toasts
            // it and tags the body `__hasError`, so treat it as a failed save.
            if ((response as any)?.__hasError) {
              const body = response as any;

              this.errorMessage =
                body?.error ??
                body?.errorMessage ??
                body?.message ??
                this.translate.instant('REPAYMENT_PLAN.SAVE_FAILED');

              this.creditService.reportSectionSaveResult({
                sectionId: this.sectionId,
                status: 'api-error',
                message: this.errorMessage,
              });

              return;
            }

            this.resultRows =
              extractResultRows(
                response,
              );

            this.resultColumns =
              extractResultColumns(
                this.resultRows,
              );

            this.resultSummary =
              extractResultSummary(
                response,
              );

            this.successMessage =
              this.translate.instant('REPAYMENT_PLAN.SAVE_SUCCESS');

            this.toast.success(this.successMessage);

            this.removedDisbursements = [];

            if (this.config) {
              // Stored back using `typ` (not `termRefCdSeq`) so a later
              // `applyConfig()` re-read via `mapDisbursementItem()` — which
              // only understands `typ` — keeps the term grouping intact.
              this.config.disbursements =
                activeDisbursements.map(
                  ({ termRefCdSeq, ...rest }) => ({
                    ...rest,
                    typ: termRefCdSeq,
                  }),
                );

              this.config.calculatedPlan =
                response;
            }

            this.creditService.reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'saved',
            });
          },

          error: (error: any) => {
            this.errorMessage =
              error?.error?.message ??
              this.translate.instant('REPAYMENT_PLAN.SAVE_FAILED');

            this.toast.error(this.errorMessage);

            console.error(
              'Save Repayment Plan API error:',
              error,
            );

            this.creditService.reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'api-error',
              message: this.errorMessage,
            });
          },
        });

    this.subscriptions.add(
      saveSubscription,
    );
  }

  private reportValidationError(message: string): void {
    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'validation-error',
      message,
    });
  }

  clearResult(): void {
    this.resultRows = [];
    this.resultColumns = [];
    this.resultSummary = [];
  }

  formatColumnLabel(
    value: string,
  ): string {
    return formatColumnLabel(value);
  }

  formatResultValue(
    value: unknown,
  ): string {
    return formatResultValue(value);
  }

  get savedDisbursementCount(): number {
    return this.disbursements.filter(
      (row) =>
        row.prpsdDsbmntSeq !== null &&
        row.prpsdDsbmntSeq !== undefined,
    ).length;
  }

  formatMonthLabel(
    value: string,
  ): string {
    const match =
      String(value ?? '').match(
        /^(\d{4})-(\d{2})$/,
      );

    if (!match) {
      return '';
    }

    const monthIndex =
      Number(match[2]) - 1;

    if (
      monthIndex < 0 ||
      monthIndex > 11
    ) {
      return '';
    }

    const monthLabel = this.translate.instant(
      `REPAYMENT_PLAN.MONTHS.${RepaymentPlan.MONTH_LABELS[monthIndex].toUpperCase()}`,
    );

    return `${monthLabel} ${match[1]}`;
  }

  trackByIndex(
    index: number,
  ): number {
    return index;
  }

  trackByColumn(
    index: number,
    column: string,
  ): string {
    return column;
  }

  private applyConfig(): void {
    if (!this.config) {
      return;
    }

    if (
      Array.isArray(
        this.config.disbursements,
      ) &&
      this.config.disbursements.length >
        0
    ) {
      this.disbursements =
        this.config.disbursements.map(
          (item) =>
            this.mapDisbursementItem(
              item,
            ),
        );

      this.syncFirstSlotMonths();
    }
  }

  private mapDisbursementItem(
    item: any,
  ): RepaymentDisbursementForm {
    return {
      prpsdDsbmntSeq:
        this.toNullableNumber(
          item?.prpsdDsbmntSeq,
        ),

      schMth:
        this.toMonthInput(
          item?.schMth,
        ),

      prpsdDsbmtAmt:
        this.toNullableNumber(
          item?.prpsdDsbmntAmt ??
            item?.prpsdDsbmtAmt,
        ),

      termRefCdSeq:
        this.toNullableNumber(item?.typ ?? item?.termRefCdSeq) ?? 0,
    };
  }

  private extractDisbursementItems(
    response: any,
  ): any[] {
    // New grouped-by-term shape: { terms: [{ rpymntTerm, disbursements: [...] }] }.
    const termsList =
      (Array.isArray(response?.terms) && response.terms) ||
      (Array.isArray(response?.data?.terms) && response.data.terms) ||
      null;

    if (termsList) {
      return termsList.flatMap((term: any) =>
        (Array.isArray(term?.disbursements) ? term.disbursements : []).map(
          (item: any) => ({
            ...item,
            typ: term?.rpymntTerm ?? 0,
          }),
        ),
      );
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (
      Array.isArray(
        response?.disbursements,
      )
    ) {
      return response.disbursements;
    }

    if (
      Array.isArray(
        response?.prpsdDsbmntRowDTOList,
      )
    ) {
      return response.prpsdDsbmntRowDTOList.map(
        (item: any) => ({
          ...item,
          typ: item?.rpymntTerm ?? 0,
        }),
      );
    }

    if (
      Array.isArray(
        response?.data?.disbursements,
      )
    ) {
      return response.data.disbursements;
    }

    if (
      Array.isArray(
        response?.data
          ?.prpsdDsbmntRowDTOList,
      )
    ) {
      return response.data
        .prpsdDsbmntRowDTOList.map(
          (item: any) => ({
            ...item,
            typ: item?.rpymntTerm ?? 0,
          }),
        );
    }

    return [];
  }

  private createEmptyDisbursement(
    termRefCdSeq = 0,
  ): RepaymentDisbursementForm {
    return {
      prpsdDsbmntSeq: null,
      schMth: '',
      prpsdDsbmtAmt: null,
      termRefCdSeq,
    };
  }

  private monthToApiDate(
    month: string,
  ): string {
    return `${month}-01`;
  }

  private toMonthInput(
    value: unknown,
  ): string {
    const dateValue =
      String(value ?? '').trim();

    const match =
      dateValue.match(
        /^(\d{4})-(\d{2})/,
      );

    return match
      ? `${match[1]}-${match[2]}`
      : '';
  }

  private isValidMonth(
    value: string,
  ): boolean {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(
      String(value ?? ''),
    );
  }

  private toNullableNumber(
    value: unknown,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numericValue =
      Number(value);

    return Number.isFinite(
      numericValue,
    )
      ? numericValue
      : null;
  }
}